import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get, kebabCase } from 'lodash';
import type { FieldProps } from 'formik';
import 'formik';
import { getInputClasses, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { CharacterCounter } from './CharacterCounter';
import { ErrorNote } from './ErrorNote';
import { Label } from './Label';
type Props = FieldProps & {
  dataTestId?: string;
  hasCharacterCounter?: boolean;
  hasValidationStatus?: boolean;
  hideErrorMessage: boolean;
  isDisabled?: boolean;
  // Keeps the label for assistive tech only, when a visible heading already introduces the field.
  isLabelHidden?: boolean;
  isRequired?: boolean;
  label?: string;
  maxLength?: number;
  placeholder?: string;
  rows?: number;
};
export const Textarea = ({
  dataTestId,
  field,
  form,
  hasCharacterCounter = false,
  hasValidationStatus = false,
  hideErrorMessage,
  isDisabled = false,
  isLabelHidden = false,
  isRequired = false,
  label,
  maxLength,
  placeholder,
  rows,
}: Props) => {
  // Textarea value defaults to empty string
  const value = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  const counterId = `counter-${kebabCase(field.name)}`;
  const showCounter = hasCharacterCounter && !!maxLength;
  const classes = getInputClasses({
    hasRadius: false,
    hasValidationStatus,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  const errorNote = !hideErrorMessage && (
    <ErrorNote
      className={showCounter ? undefined : 'mx-15'}
      errorMessage={form.errors[field.name] as string | undefined}
      errorNoteId={errorNoteId}
      isErrored={isErrored}
    />
  );
  return (
    <Fragment>
      {!!label && (
        <Label
          className={classnames('mx-15 mb-5', isLabelHidden && 'sr-only')}
          htmlFor={field.name}
          isErrored={isErrored}
          isRequired={isRequired}
          label={label}
        />
      )}
      <div className="relative isolate flex w-full">
        <textarea
          aria-describedby={showCounter ? `${errorNoteId} ${counterId}` : errorNoteId}
          aria-invalid={isErrored}
          aria-required={isRequired}
          autoComplete="off"
          className={classnames(classes, 'rounded-12')}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={field.name}
          maxLength={maxLength}
          rows={rows}
          {...(placeholder
            ? {
                placeholder,
              }
            : null)}
          {...field}
          value={value}
        />
        {hasValidationStatus && !isDisabled && (
          <ImageValidationStatus
            className="absolute end-0 top-0 mx-20 mt-15"
            isErrored={isErrored}
            isValidated={isValidated}
          />
        )}
      </div>
      {showCounter ? (
        <div className="mx-15 flex justify-between gap-10">
          {errorNote}
          <CharacterCounter
            className="ms-auto"
            id={counterId}
            length={value.length}
            maxLength={maxLength}
          />
        </div>
      ) : (
        errorNote
      )}
    </Fragment>
  );
};
